import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { HeldPeriod } from "features/trading/heldPeriods";
import { Bucket, BucketNumberEnum } from "features/trading/buckets";
import { BUCKET_DESCRIPTIONS } from "./constants";
import { DeleteBucketConfirmModal } from "./DeleteBucketConfirmModal";
import { BucketAddAssetRow } from "./BucketAddAssetRow";
import { BucketCreateButton } from "./BucketCreateButton";
import { BucketHistorySummary } from "./BucketHistorySummary";
import { BucketMembersList } from "./BucketMembersList";
import { BucketToolbarActions } from "./BucketToolbarActions";
import { useBucketActions } from "./useBucketActions";

interface BucketEditModalProps {
  open: boolean;
  onClose: () => void;
  accountId: number | null | undefined;
  accountLabel: string;
  number: BucketNumberEnum;
  bucket: Bucket | undefined;
  // Passed from the report settings; absent on the client page's Buckets tab.
  heldPeriods?: HeldPeriod[];
  // Show each member's "still in the account's holdings" status. Set by the
  // report settings only — Bucket 3's client-page editor doesn't carry it.
  showHoldingStatus?: boolean;
}

/**
 * Popup editor for a single bucket — offers creation when the bucket
 * doesn't exist yet, otherwise the same add/remove/star/include controls
 * as the client-page Buckets tab (shared components).
 */
export const BucketEditModal = ({
  open,
  onClose,
  accountId,
  accountLabel,
  number,
  bucket,
  heldPeriods,
  showHoldingStatus = false,
}: BucketEditModalProps) => {
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const actions = useBucketActions(accountId, number, bucket, heldPeriods);
  const isBucket3 = number === BucketNumberEnum.BUCKET_3;

  const handleDelete = async () => {
    const ok = await actions.removeBucket();
    if (ok) setIsConfirmDeleteOpen(false);
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Stack height="100%" alignItems="center" justifyContent="center">
          <Box style={{ width: "560px" }}>
            <Card>
              <Box
                display="flex"
                alignItems="center"
                paddingX={2}
                paddingY={1.5}
                gap={1}
              >
                <Typography variant="h6" color="primary.main">
                  Bucket {number}
                </Typography>
                <Chip size="small" variant="outlined" label={accountLabel} />
                <Box flexGrow={1} />
                <BucketToolbarActions
                  bucket={bucket}
                  actions={actions}
                  hasHeldPeriods={!!heldPeriods?.length}
                  onDeleteClick={() => setIsConfirmDeleteOpen(true)}
                />
              </Box>
              <Divider />
              {!bucket ? (
                <Stack alignItems="center" gap={1.5} sx={{ padding: 4 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    No Bucket {number} for this account yet.{" "}
                    {BUCKET_DESCRIPTIONS[number]}
                  </Typography>
                  <BucketCreateButton
                    number={number}
                    accountId={accountId}
                    actions={actions}
                  />
                </Stack>
              ) : (
                <>
                  <Box paddingX={2} paddingTop={1.5}>
                    <BucketHistorySummary
                      bucket={bucket}
                      actions={actions}
                      showHoldingStatus={showHoldingStatus}
                    />
                  </Box>
                  {!isBucket3 && accountId && (
                    <Box paddingX={2} paddingTop={1.5}>
                      <BucketAddAssetRow
                        accountId={accountId}
                        number={number}
                        actions={actions}
                        heldPeriods={heldPeriods}
                      />
                    </Box>
                  )}
                  <Box padding={1}>
                    <BucketMembersList
                      bucket={bucket}
                      actions={actions}
                      maxHeight={360}
                      showHoldingStatus={showHoldingStatus}
                      // Only with periods: without them every member's
                      // `held_in_period` is False, which would file the
                      // whole bucket under "not held in the period".
                      splitHeldInPeriod={!!heldPeriods?.length}
                    />
                  </Box>
                </>
              )}
              <Divider />
              <Box display="flex" justifyContent="flex-end" padding={1.5}>
                <Button onClick={onClose}>Close</Button>
              </Box>
            </Card>
          </Box>
        </Stack>
      </Modal>
      <DeleteBucketConfirmModal
        open={isConfirmDeleteOpen}
        number={number}
        bucket={bucket}
        confirming={actions.isDeleting}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};
